import { useState } from "react";
import { useLocation } from "wouter";

/**
 * Split-screen login, inspired by the original buyer.zeemart.co/login
 * layout (form lane + brand-promise lane) but rewritten better:
 *
 *   - The brand-promise lane is Aiko's voice ("I run your procurement,
 *     not your paperwork") on a gradient mint surface, not a stock chef
 *     photo. The promise is what the agent does, not what the kitchen is.
 *   - The form is the same shape (Welcome back, email + password,
 *     big green button, forgot password) so muscle memory survives,
 *     but with sharper focus-ring + micro-animation + plain-English
 *     error copy ("Sign-in did not work. Please try again.") instead
 *     of upstream stack text.
 *   - Footer mirrors the original (Terms / Privacy / Part of nomni).
 */
export function Login() {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ZeemartId: email, password: pass }),
      });
      if (!r.ok) {
        const t = await r.text();
        setErr(t || "Sign-in did not work. Please try again.");
        return;
      }
      navigate("/app/dashboard");
    } catch {
      setErr("Something interrupted that. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main role="split-screen" className="min-h-[100dvh] grid md:grid-cols-2 bg-app-bg">
      {/* Form lane */}
      <section className="flex flex-col px-8 md:px-14 py-8 pad-safe">
        <header>
          <a href="/" className="inline-flex items-center gap-2">
            <span aria-hidden className="inline-flex w-8 h-8 rounded-full bg-nomni-green/20 items-center justify-center">
              <span className="w-4 h-4 rounded-full bg-nomni-green block" />
            </span>
            <span className="text-xl tracking-tight">
              <span className="font-medium text-ink">nomni</span>{" "}
              <span className="text-nomni-green font-medium">Procure</span>
            </span>
          </a>
        </header>

        <div className="flex-1 flex items-center">
          <form
            onSubmit={onSubmit}
            className="w-full max-w-sm mx-auto space-y-5 motion-safe:animate-[fadeIn_400ms_ease-out]"
          >
            <div>
              <h1 className="font-display text-3xl text-ink">Welcome back</h1>
              <p className="text-ink-2 text-sm mt-1">Sign in with your Zeemart buyer credentials.</p>
            </div>

            <label className="block space-y-1">
              <span className="text-sm text-ink-2">Email address</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-field-border bg-canvas-2 px-3 py-2.5 text-ink outline-none transition focus:border-nomni-green focus:ring-2 focus:ring-mint"
                required
                autoComplete="email"
                autoFocus
              />
            </label>

            <label className="block space-y-1">
              <span className="text-sm text-ink-2">Password</span>
              <input
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                className="w-full rounded-md border border-field-border bg-canvas-2 px-3 py-2.5 text-ink outline-none transition focus:border-nomni-green focus:ring-2 focus:ring-mint"
                required
                autoComplete="current-password"
              />
            </label>

            {err && (
              <div
                role="alert"
                className="rounded-md border border-bad/40 bg-bad/10 text-bad px-3 py-2 text-sm"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !email || !pass}
              className="w-full rounded-md bg-nomni-green text-canvas-2 px-4 py-2.5 font-medium hover:brightness-110 transition disabled:opacity-60 disabled:cursor-not-allowed shadow-sm shadow-nomni-green/30"
            >
              {busy ? "Signing in..." : "Log in"}
            </button>

            <div className="text-center">
              <a className="text-sm text-spinach hover:underline" href="https://buyer.zeemart.co/forgot-password">
                Forgot password?
              </a>
            </div>
          </form>
        </div>

        <footer className="text-xs text-ink-mute flex items-center justify-between">
          <div className="space-x-4">
            <a href="https://nomni.co/terms" className="hover:text-ink-2">Terms</a>
            <a href="https://nomni.co/privacy" className="hover:text-ink-2">Privacy</a>
          </div>
          <div className="inline-flex items-center gap-1.5">
            <span>Part of</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-nomni-green block" />
              <span className="font-medium text-ink-2">nomni</span>
            </span>
          </div>
        </footer>
      </section>

      {/* Brand-promise lane */}
      <aside
        aria-hidden
        className="hidden md:flex relative overflow-hidden bg-gradient-to-br from-mint via-cream to-canvas-2 px-14 py-12 items-center"
      >
        <div className="relative z-10 max-w-md">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-spinach font-medium px-2 py-1 rounded-md border border-nomni-green/30 bg-canvas-2/80">
            <span aria-hidden>✦</span> Meet Aiko
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-seaweed mt-5 leading-[1.05]">
            I run your procurement.
            <br />
            <span className="text-nomni-green">Not your paperwork.</span>
          </h2>
          <p className="text-ink-2 mt-5 text-base leading-relaxed">
            Sign in once. By tomorrow she knows your outlets, your suppliers,
            your cadence. She drafts your orders and pauses at the gate that
            can't be undone.
          </p>

          <ul className="mt-7 space-y-3 text-sm text-ink-2">
            <PromiseRow text="Reads your real orders and invoices through your Zeemart account." />
            <PromiseRow text="Drafts the next purchase order against last week's usage." />
            <PromiseRow text="Asks before she sends. Always." />
          </ul>
        </div>

        {/* Subtle decorative arcs */}
        <svg
          aria-hidden
          viewBox="0 0 600 600"
          className="absolute -right-40 -bottom-40 w-[44rem] opacity-30 pointer-events-none"
        >
          <circle cx="300" cy="300" r="240" fill="none" stroke="oklch(0.74 0.18 145)" strokeWidth="1" />
          <circle cx="300" cy="300" r="180" fill="none" stroke="oklch(0.74 0.18 145)" strokeWidth="1" />
          <circle cx="300" cy="300" r="120" fill="none" stroke="oklch(0.74 0.18 145)" strokeWidth="1" />
        </svg>
      </aside>

      {/* Local keyframes for the fade-in (Tailwind v4 inline) */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
      `}</style>
    </main>
  );
}

function PromiseRow({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2.5">
      <span
        aria-hidden
        className="inline-flex w-5 h-5 mt-0.5 rounded-full bg-nomni-green/20 items-center justify-center shrink-0"
      >
        <span className="w-2 h-2 rounded-full bg-nomni-green block" />
      </span>
      <span>{text}</span>
    </li>
  );
}
